import {Observable} from 'rxjs';
import i18next from 'i18next';

import {combineEpics} from 'web-app/util/redux-observable';
import {showDeleteDialogObservable, DialogChoice} from 'web-app/react/dialog';
import InboxApi from 'web-app/api/inbox-api';
import {updateSuccess} from 'web-app/react/global-event/actions';
import {markConversationAsRead} from 'web-app/react/inbox/conversation-center/actions';
import * as GlobalEventActions from 'web-app/react/global-event/actions';

import * as Actions from './actions';
import * as Api from './api';
import {MESSAGES_PER_PAGE} from './helpers';

const deleteMessages = action$ =>
    action$.ofType(Actions.deleteMessages.type).mergeMap(({payload}) => {
        return showDeleteDialogObservable({
            title: '',
            description: i18next.t('messages.confirmDelete', {count: payload.messageIds.size}),
        }).mergeMap(selection => {
            if (selection === DialogChoice.OK) {
                return Api.deleteMessages(payload.messageIds)
                    .map(response =>
                        Actions.deleteMessagesSuccess.action(response, payload.messageIds, payload.allMessagesLength),
                    )
                    .catch(e => Observable.of(Actions.deleteMessagesFailed.action(e)));
            } else {
                return Observable.of(Actions.deleteMessagesAborted.action());
            }
        });
    });

const archiveConversation = action$ =>
    action$.ofType(Actions.archiveConversation.type).mergeMap(({payload}) => {
        return Api.archiveConversation(payload.conversationId)
            .flatMap(response => [
                Actions.archiveConversationSuccess.action(response),
                updateSuccess.action(i18next.t('messages.archiveSuccess')),
            ])
            .catch(e => Observable.of(Actions.archiveConversationFailed.action(e)));
    });

const unarchiveConversation = action$ =>
    action$.ofType(Actions.unarchiveConversation.type).mergeMap(({payload}) => {
        return Api.unarchiveConversation(payload.conversationId)
            .flatMap(response => [
                Actions.unarchiveConversationSuccess.action(response, payload.conversationId),
                updateSuccess.action(i18next.t('messages.unarchiveSuccess')),
            ])
            .catch(e => Observable.of(Actions.unarchiveConversationFailed.action(e)));
    });

const fetchConversation = (action$, state$) =>
    action$.ofType(Actions.fetchConversation.type).mergeMap(({payload}) => {
        // Compute limit to the amount of messages currently loaded plus one.
        // This logic is specifically needed when a new message in received trough push-notifications.
        const limit = Math.max(
            MESSAGES_PER_PAGE,
            1 + (state$.value.inbox.conversation?.get('conversation')?.get('messages')?.size ?? 0),
        );
        return InboxApi.showConversationObservable(payload.conversationId, limit, payload.suppressErrors)
            .map(response => Actions.fetchConversationSuccess.action(response, payload.reload))
            .catch(e => Observable.of(Actions.fetchConversationFailed.action(e)));
    });

const fetchPagedConversation = action$ =>
    action$.ofType(Actions.fetchPagedConversation.type).mergeMap(({payload}) =>
        InboxApi.showPagedConversationObservable(
            payload.conversationId,
            MESSAGES_PER_PAGE,
            payload.cursor,
            payload.suppressErrors,
        )
            .map(response => Actions.fetchPagedConversationSuccess.action(response, payload.reload))
            .catch(e => Observable.of(Actions.fetchPagedConversationFailed.action(e))),
    );

const unreadConversation = action$ =>
    action$.ofType(Actions.markAsUnread.type).mergeMap(({payload}) =>
        Api.unreadConversation(payload.conversationId)
            .flatMap(() => [
                Actions.markAsUnreadSuccess.action(payload.conversationId),
                GlobalEventActions.updateSuccess.action(i18next.t('messages.markedAsUnread')),
            ])
            .catch(e => Observable.of(Actions.markAsUnreadFailed.action(e))),
    );

const markConversationAsReadOnFetch = action$ =>
    action$
        .ofType(Actions.fetchConversationSuccess.type)
        .filter(({payload}) => {
            // We want to only emit markConversationAsRead for conversations that were unread in fetched data.
            // The 'unread' flag in the response payload is true _only_ the first time fetching an unread conversation.
            const wasUnread = payload?.response?.unread;

            return wasUnread;
        })
        .mergeMap(({payload}) => {
            return Observable.of(markConversationAsRead.action(payload.response.conversationId));
        });

const fetchAfterMessageId = action$ =>
    action$.ofType(Actions.fetchAfterMessageId.type).mergeMap(({payload}) =>
        InboxApi.showAfterMessageIdActionObservable(
            payload.conversationId,
            payload.messageId,
            MESSAGES_PER_PAGE,
            payload.suppressErrors,
        )
            .map(response => Actions.fetchConversationSuccess.action(response, payload.reload))
            .catch(e => Observable.of(Actions.fetchConversationFailed.action(e))),
    );

export default combineEpics(
    deleteMessages,
    archiveConversation,
    unarchiveConversation,
    fetchConversation,
    fetchPagedConversation,
    markConversationAsReadOnFetch,
    unreadConversation,
    fetchAfterMessageId,
);
